import { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import { trackEvent, mapApiErrorToCode } from '../services/analytics';
import {
  REPORT_CATEGORIES,
  pinTypeToApiType,
  type ReportablePinType,
  type ReportCategory,
} from '../services/report';

interface ReportPinModalProps {
  pinType: ReportablePinType;
  pinId: string;
  onClose: () => void;
  onSubmitted: () => void;
}

const PIN_TYPE_LABEL: Record<ReportablePinType, string> = {
  spot: 'parking spot',
  rent: 'price data pin',
};

function isDuplicateError(message: string): boolean {
  const m = message.toLowerCase();
  return m.includes('already reported') || m.includes('duplicate');
}

export default function ReportPinModal({ pinType, pinId, onClose, onSubmitted }: ReportPinModalProps) {
  const [category, setCategory] = useState<ReportCategory>('inaccurate_location');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (error) {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [error]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    trackEvent('pin_report_attempt', { pin_type: pinType, reason_category: category });
    try {
      await api.reportPin(pinTypeToApiType(pinType), pinId, category);
      trackEvent('pin_reported', {
        pin_type: pinType,
        reason_category: category,
        result: 'success',
      });
      onSubmitted();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit report';
      let result: 'duplicate' | 'rate_limited' | 'validation' | 'network' | 'server' | 'unknown';
      if (isDuplicateError(message)) {
        result = 'duplicate';
      } else {
        const code = mapApiErrorToCode(err);
        result = code === 'rate_limit' ? 'rate_limited' : code;
      }
      trackEvent('pin_reported', {
        pin_type: pinType,
        reason_category: category,
        result,
      });
      setError(
        result === 'duplicate'
          ? 'You have already reported this pin.'
          : result === 'rate_limited'
          ? 'You\'ve reached the daily report limit. Try again tomorrow.'
          : result === 'network'
          ? 'Network error. Please check your connection and try again.'
          : message
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[150] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden">
        <div className="flex-shrink-0 p-4 border-b flex items-center justify-between bg-gray-50 rounded-t-2xl">
          <h2 className="font-semibold text-gray-900">Report this {PIN_TYPE_LABEL[pinType]}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
            className="text-gray-500 hover:text-gray-700 text-xl leading-none disabled:opacity-50"
          >
            &times;
          </button>
        </div>

        <form id="report-pin-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 min-h-0 space-y-4">
          <p className="text-sm text-gray-600">
            Help us keep Parksy accurate. Pick the reason that best describes the problem.
          </p>

          {error && (
            <div
              ref={errorRef}
              className="bg-red-50 text-red-800 text-sm p-3 rounded-lg border border-red-200"
            >
              {error}
            </div>
          )}

          <div>
            <label
              htmlFor="report-category"
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              Reason
            </label>
            <select
              id="report-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as ReportCategory)}
              disabled={submitting}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm bg-white focus:ring-2 focus:ring-parksy-500 focus:border-transparent disabled:opacity-60"
            >
              {REPORT_CATEGORIES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <p className="text-xs text-gray-500">
            Reports are anonymous and tied to your IP only to prevent duplicates. 3 reports from
            different people will hide the pin automatically.
          </p>
        </form>

        <div className="flex-shrink-0 p-4 border-t bg-gray-50 rounded-b-2xl flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="report-pin-form"
            disabled={submitting}
            className="flex-1 px-4 py-2.5 text-white rounded-lg text-sm font-medium bg-red-600 hover:bg-red-700 disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Submit report'}
          </button>
        </div>
      </div>
    </div>
  );
}
